<template>
    <span>
        <b-button v-if="showButton" variant="info" :size="buttonSize" :disabled="!hasDrug"
            v-b-tooltip.hover title="View the patient education sheet content (Ctrl+E)"
            @click="openModal">
            <slot name="button-content">Patient Education</slot>
        </b-button>

        <b-modal :id="`${id}-modal`" size="lg" title="Patient Education" hide-footer scrollable
            @show="onShow">
            <div v-if="loading" class="text-center my-4">
                <b-spinner label="Loading" />
                <div class="mt-2">Loading patient education...</div>
            </div>
            <div v-else-if="loadError" class="text-danger my-3">
                {{ loadError }}
            </div>
            <template v-else>
                <p v-if="sheet && sheet.formattedNDC" class="text-muted mb-3">
                    <strong>NDC:</strong> {{ sheet.formattedNDC }}
                </p>
                <div v-if="!sections.length" class="text-muted my-3">
                    No patient education is available for this drug.
                </div>
                <div v-for="(section, index) in sections" :key="index" class="pat-ed-section mb-3">
                    <h6 v-if="section.header" class="font-weight-bold">{{ section.header }}</h6>
                    <p v-if="section.body" class="pat-ed-body mb-1">{{ section.body }}</p>
                    <p v-if="section.footer" class="text-muted small mb-0">{{ section.footer }}</p>
                </div>
            </template>
        </b-modal>
    </span>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { DrugSource } from "@/models/Drug/DrugProduct";
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import {
    fetchPatientEducation,
    toSections,
    PatientEducationSheet,
    PatientEducationSection,
} from "@/services/PatientEducationService";

// Self-contained modal that shows the patient-education-sheet sections for the current drug,
// opened by its own button or the Ctrl+E hotkey. Drop onto any page that has a (drugId, source)
// pair in context. See SD-4306.
@Component({
    name: "PatientEducationModalComponent",
})
export default class PatientEducationModalComponent extends Vue {
    @Prop({ default: 'patient-education' }) private id!: string;
    @Prop() private drugId!: number | null;
    @Prop({ default: () => DrugSource.GsddFederal }) private drugSource!: DrugSource;
    @Prop({ default: true }) private showButton!: boolean;
    @Prop({ default: 'sm' }) private buttonSize!: string;

    private sheet: PatientEducationSheet | null = null;
    private loading = false;
    private loadError = "";

    mounted() {
        document.addEventListener('keydown', this.preventDefaultForHotkeys);
        document.addEventListener('keyup', this.hotKeysListener);
    }

    beforeDestroy() {
        document.removeEventListener("keydown", this.preventDefaultForHotkeys);
        document.removeEventListener("keyup", this.hotKeysListener);
    }

    get hasDrug(): boolean {
        return this.drugId != null && this.drugId > 0;
    }

    get sections(): PatientEducationSection[] {
        return toSections(this.sheet);
    }

    // Ignore the hotkey while the user is typing in a field.
    private isTypingTarget(e: KeyboardEvent): boolean {
        const el = e.target as HTMLElement | null;
        if (!el) return false;
        const tag = el.tagName;
        return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
    }

    preventDefaultForHotkeys(e: KeyboardEvent) {
        if (!this.hasDrug) return;
        if (e.ctrlKey && e.key === "e" && !this.isTypingTarget(e)) e.preventDefault();
    }

    hotKeysListener(e: KeyboardEvent) {
        if (e.ctrlKey && e.key === "e" && !this.isTypingTarget(e)) this.openModal();
    }

    openModal() {
        if (!this.hasDrug) return;
        this.$bvModal.show(`${this.id}-modal`);
    }

    async onShow() {
        if (!this.hasDrug) return;
        this.loading = true;
        this.loadError = "";
        try {
            this.sheet = await fetchPatientEducation(this.drugId as number, this.drugSource);
        } catch (err) {
            this.sheet = null;
            this.loadError = "No patient education is available for this drug.";
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.loading = false;
        }
    }
}
</script>

<style scoped>
.pat-ed-body {
    white-space: pre-line;
}
</style>
